import { Field } from 'formik'
import type { ReactNode } from 'react'
import React from 'react'
import type { Editor } from 'tinymce'

import { TinyMCE } from '@components/adapters/inputs/TinyMCE'
import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { Fieldset, Input, Subsection, useModalContext } from '@fourthwall/components'
import { isWebView } from '@fourthwall/utils'

import { ImageUploadModal } from '../../components/ImageUploadModal'
import { FormFields } from '../../types'

type Props = {
  disabled?: boolean
  children?: ReactNode
}

export const CommonFields = ({ disabled, children }: Props) => {
  const modal = useModalContext()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  return (
    <Subsection title="Post details">
      <Fieldset>
        <Fieldset.Row>
          <Fieldset.Item>
            <Field
              name={FormFields.title}
              component={Input}
              label="Post title"
              required
              disabled={disabled}
              fullWidth
            />
          </Fieldset.Item>
        </Fieldset.Row>
        <Fieldset.Row>
          <Fieldset.Item>
            <Field
              name={FormFields.description}
              component={TinyMCE}
              resize={!isWebView()}
              buttons={{
                customImage: true,
                code: !isWebView() && dashboardFeatureFlags.membershipsPostHtml.enabled,
              }}
              invalidElements="div"
              statusbar={!isWebView()}
              height={isWebView() ? undefined : 185}
              placeholder="Post description"
              disabled={disabled}
              onSetup={(editor: Editor) => {
                editor.ui.registry.addButton('customImage', {
                  icon: 'image',
                  onAction: () => {
                    const selectedNode = editor.selection.getNode()

                    if (selectedNode.nodeName === 'IMG') {
                      return modal.open(ImageUploadModal, {
                        src: selectedNode.getAttribute('src') || '',
                        width: selectedNode.getAttribute('width')
                          ? Number(selectedNode.getAttribute('width'))
                          : undefined,
                        height: selectedNode.getAttribute('height')
                          ? Number(selectedNode.getAttribute('height'))
                          : undefined,
                        alt: selectedNode.getAttribute('alt') || '',
                        onConfirm: (src, width, height, alt) =>
                          editor.selection.setContent(
                            `<img src="${src}" width="${width}" height="${height}" alt="${alt}"/>`,
                          ),
                      })
                    }

                    modal.open(ImageUploadModal, {
                      onConfirm: (src, width, height, alt) =>
                        editor.insertContent(
                          `<p><img src="${src}" width="${width}" height="${height}" alt="${alt}" /></p>`,
                        ),
                    })
                  },
                })
              }}
            />
          </Fieldset.Item>
        </Fieldset.Row>
        {children && <Fieldset.Row>{children}</Fieldset.Row>}
      </Fieldset>
    </Subsection>
  )
}
